import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Calculator, ClipboardCheck, GitCompareArrows, LayoutDashboard, Receipt, ScrollText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Nav, NavItem, NavLink, TabContent, TabPane } from '@/components/ui-compat'
import * as Query from '../../api/gql/query'
import { useTabSwitch } from '../../hooks'
import { useMyPermissions } from '../../hooks/useMyPermissions'
import BudgetTab from './budget/BudgetTab'
import BudgetTabSkeleton from './budget/BudgetTabSkeleton'
import ChangesTab from './changes/ChangesTab'
import ContractsTab from './contracts/ContractsTab'
import MyContracts from './contracts/MyContracts'
import InvoicesTab from './invoices/InvoicesTab'
import OverviewTab from './overview/OverviewTab'
import OverviewTabSkeleton from './overview/OverviewTabSkeleton'
import PendingTab from './pending/PendingTab'
import RfqsTab from './rfq/RfqsTab'
import type { CostPendingAction } from './types'

const CostControl: React.FC = () => {
  const { t } = useTranslation()
  const { data: meData } = useQuery(Query.ME)
  const projectId: string | undefined = meData?.me?.currentProject?.id
  const { can, recordScoped, loading: rightsLoading } = useMyPermissions(projectId)
  const canView = can('VIEW', 'COST_CONTROL')
  const { data: pendingData } = useQuery<{ costPendingActions: CostPendingAction[] }>(Query.COST_PENDING_ACTIONS, {
    variables: { projectId },
    skip: !projectId || !canView,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const pendingCount = pendingData?.costPendingActions.length ?? 0
  const [tab, setTab] = useTabSwitch('overview')
  const changeManagement = tab === 'changes' || tab === 'rfqs'

  if (!projectId || rightsLoading) return tab === 'overview' ? <OverviewTabSkeleton /> : <BudgetTabSkeleton />
  // 1b-B spec §8: a contractor's representative gets his contracts, no tabs.
  if (!canView && recordScoped('COST_CONTROL')) return <MyContracts projectId={projectId} />

  return (
    <div className="tab">
      <Nav tabs>
        <NavItem>
          <NavLink className={cn('flex items-center', { active: tab === 'overview' })} onClick={() => setTab('overview')}>
            <LayoutDashboard className="h-4 w-4 mr-2" />
            {t('CostControl_tab_overview')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink className={cn('flex items-center', { active: tab === 'budget' })} onClick={() => setTab('budget')}>
            <Calculator className="h-4 w-4 mr-2" />
            {t('CostControl_tab_budget')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink className={cn('flex items-center', { active: tab === 'contracts' })} onClick={() => setTab('contracts')}>
            <ScrollText className="h-4 w-4 mr-2" />
            {t('CostControl_tab_contracts')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink className={cn('flex items-center', { active: changeManagement })} onClick={() => !changeManagement && setTab('changes')}>
            <GitCompareArrows className="h-4 w-4 mr-2" />
            {t('CostControl_tab_change_management')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink className={cn('flex items-center', { active: tab === 'invoices' })} onClick={() => setTab('invoices')}>
            <Receipt className="h-4 w-4 mr-2" />
            {t('CostControl_tab_invoices')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink className={cn('flex items-center', { active: tab === 'pending' })} onClick={() => setTab('pending')}>
            <ClipboardCheck className="h-4 w-4 mr-2" />
            {t('CostControl_tab_pending')}
            {pendingCount > 0 && <Badge variant="warning" className="ml-2">{pendingCount}</Badge>}
          </NavLink>
        </NavItem>
      </Nav>
      <TabContent activeTab={changeManagement ? 'changes' : tab}>
        <TabPane tabId="overview">
          {tab === 'overview' && <OverviewTab projectId={projectId} onOpenPending={() => setTab('pending')} />}
        </TabPane>
        <TabPane tabId="budget">
          <BudgetTab projectId={projectId} />
        </TabPane>
        <TabPane tabId="contracts">
          {tab === 'contracts' && <ContractsTab projectId={projectId} />}
        </TabPane>
        <TabPane tabId="changes">
          {/* Freelo #33300943: changes and RFQs are one change-management tab; the sub-tab keeps the old ?tab= values, so links still land. */}
          {changeManagement && (
            <>
              <Tabs value={tab} onValueChange={setTab} className="pt-2">
                <TabsList className="h-8">
                  <TabsTrigger value="changes" className="h-6 text-sm">{t('CostControl_tab_changes')}</TabsTrigger>
                  <TabsTrigger value="rfqs" className="h-6 text-sm">{t('CostControl_tab_rfqs')}</TabsTrigger>
                </TabsList>
              </Tabs>
              {tab === 'changes' ? <ChangesTab projectId={projectId} /> : <RfqsTab projectId={projectId} />}
            </>
          )}
        </TabPane>
        <TabPane tabId="invoices">{tab === 'invoices' && <InvoicesTab projectId={projectId} />}</TabPane>
        <TabPane tabId="pending">{tab === 'pending' && <PendingTab projectId={projectId} />}</TabPane>
      </TabContent>
    </div>
  )
}
export default CostControl
